/**
 * Portal.jsx
 * Authenticated portal shell.
 * Rendered only after MSAL confirms a valid session.
 */

import React from "react";
import { useMsal } from "@azure/msal-react";
import { usePortalUser } from "../hooks/usePortalUser";
import AppCard from "./AppCard";

export default function Portal() {
  const { instance } = useMsal();
  const { user, role, authorizedApps, loading, error } = usePortalUser();

  function handleSignOut() {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="portal-loading">
        <div className="portal-loading__spinner" />
        <p className="portal-loading__text">Verifying credentials…</p>
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="portal-error">
        <h2>Unable to load your workspace</h2>
        <p>{error}</p>
        <button onClick={handleSignOut} className="btn btn--ghost">Sign out and try again</button>
      </div>
    );
  }

  // ── No role / unauthorized ─────────────────────────────────────────────────
  if (!role) {
    return (
      <div className="portal-error">
        <h2>Access not configured</h2>
        <p>
          Your account ({user?.email}) is authenticated but has not been assigned
          to a portal access group. Contact your administrator.
        </p>
        <button onClick={handleSignOut} className="btn btn--ghost">Sign out</button>
      </div>
    );
  }

  // ── Authenticated + authorized ─────────────────────────────────────────────
  return (
    <div className="portal">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="portal-header">
        <div className="portal-header__brand">
          <span className="portal-header__logo">H2</span>
          <div className="portal-header__brand-text">
            <span className="portal-header__company">H2 Fin</span>
            <span className="portal-header__tagline">Harness Financial Platform</span>
          </div>
        </div>

        <div className="portal-header__user">
          <div className="portal-header__user-meta">
            <span className="portal-header__user-name">{user?.name}</span>
            <span className="portal-header__user-role">{ROLE_DISPLAY[role]}</span>
          </div>
          <div className="portal-header__avatar" aria-hidden="true">
            {user?.initials}
          </div>
          <button
            className="btn btn--ghost btn--sm"
            onClick={handleSignOut}
            title="Sign out"
          >
            Sign out
          </button>
        </div>
      </header>

      {/* ── Portal body ────────────────────────────────────────────────────── */}
      <main className="portal-main">
        <div className="portal-welcome">
          <h1 className="portal-welcome__heading">
            Good {getTimeOfDay()}, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="portal-welcome__sub">
            {authorizedApps.length} tool{authorizedApps.length !== 1 ? "s" : ""} available in your workspace.
          </p>
        </div>

        <div className="portal-grid">
          {authorizedApps.map(app => (
            <AppCard key={app.id} app={app} />
          ))}
        </div>
      </main>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="portal-footer">
        <span>H2 Fin © {new Date().getFullYear()}</span>
        <span className="portal-footer__sep">·</span>
        <span>Licensed by Harness Financial LLC</span>
        <span className="portal-footer__sep">·</span>
        <span>Secured by Microsoft Entra ID</span>
      </footer>
    </div>
  );
}

const ROLE_DISPLAY = {
  admin:  "Administrator",
  user:   "Advisor",
  branch: "Branch / OSJ",
};

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}
